import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { SearchInput } from "@/components/common/SearchInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { LayoutGrid, Table2, Plus, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Directory() {
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useApp();
  const { data: emps, isLoading } = useQuery({ queryKey: ["hrms-directory"], queryFn: () => get("/hrms/directory") });

  const remove = useMutation({
    mutationFn: (id) => del(`/users/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["hrms-directory"] }); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Employee removed"); },
    onError: () => toast.error("This employee is protected and cannot be removed"),
  });

  const departments = useMemo(
    () => Array.from(new Set((emps || []).map((e) => e.department?.name).filter(Boolean))).sort(),
    [emps]
  );
  const statuses = useMemo(
    () => Array.from(new Set((emps || []).map((e) => e.status).filter(Boolean))).sort(),
    [emps]
  );

  const filtered = useMemo(() => {
    return (emps || []).filter((e) => {
      if (departmentFilter !== "all" && e.department?.name !== departmentFilter) return false;
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      if (search && !`${e.name} ${e.designation}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [emps, departmentFilter, statusFilter, search]);

  return (
    <div>
      <PageHeader title="Employee Directory" subtitle="All team members across departments.">
        <div className="flex rounded-lg border border-border bg-card p-0.5">
          <button data-testid="dir-table" onClick={() => setView("table")} className={cn("rounded-md px-3 py-1.5 text-sm", view === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}><Table2 className="h-4 w-4" /></button>
          <button data-testid="dir-card" onClick={() => setView("card")} className={cn("rounded-md px-3 py-1.5 text-sm", view === "card" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}><LayoutGrid className="h-4 w-4" /></button>
        </div>
        {can("hrms", "create") && (
          <Button data-testid="add-employee-btn" onClick={() => navigate("/hrms/directory/new")}>
            <Plus className="mr-1.5 h-4 w-4" />Add Employee
          </Button>
        )}
      </PageHeader>

      <div className="mb-4 flex items-center gap-3">
        <SearchInput testid="directory-search" placeholder="Search name or designation" value={search} onChange={setSearch} />
        <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
          <SelectTrigger className="w-44" data-testid="directory-department-filter"><SelectValue placeholder="All departments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="directory-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <Skeleton className="h-64" /> : filtered.length === 0 ? (
        <EmptyState icon={Users} title={(emps || []).length === 0 ? "No employees" : "No matching employees"}
          subtitle={(emps || []).length === 0 ? undefined : "Adjust your search or filters."} />
      ) : view === "table" ? (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Name</th><th className="px-4 py-3 font-semibold">Department</th>
              <th className="px-4 py-3 font-semibold">Designation</th><th className="px-4 py-3 font-semibold">Status</th>
              {can("hrms", "delete") && <th className="px-4 py-3"></th>}</tr></thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} data-testid={`emp-row-${e.id}`} onClick={() => navigate(`/hrms/directory/${e.id}`)} className="group cursor-pointer border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><Avatar user={e} size="sm" /><span className="font-medium">{e.name}</span>{e.protected && <StatusBadge family="slate" label="Protected" />}{e.in_termination_zone && <StatusBadge family="red" label="Termination Zone" />}</div></td>
                  <td className="px-4 py-3 text-muted-foreground">{e.department?.name}</td>
                  <td className="px-4 py-3">{e.designation}</td>
                  <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                  {can("hrms", "delete") && (
                    <td className="px-4 py-3 text-right" onClick={(ev) => ev.stopPropagation()}>
                      {!e.protected && (
                        <ConfirmDelete testid={`del-emp-${e.id}`} title={`Remove ${e.name}?`}
                          trigger={<button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                          onConfirm={() => remove.mutate(e.id)} />
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {filtered.map((e) => (
            <div key={e.id} className="flex flex-col items-center rounded-xl border border-border bg-card p-5 text-center">
              <Avatar user={e} size="lg" />
              <div className="mt-3 font-display font-semibold">{e.name}</div>
              <div className="text-xs text-muted-foreground">{e.designation}</div>
              <div className="mt-2"><StatusBadge status={e.status} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
